import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import { FaArrowRight, FaCheckCircle, FaTerminal, FaPlay, FaCopy, FaCheck, FaPause, FaCode, FaWindowMaximize, FaWindowMinimize } from 'react-icons/fa';
import { TypeAnimation } from 'react-type-animation';
import './ServicesPage.css';

const ServicesPage = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);
  const [copied, setCopied] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMaximized, setIsMaximized] = useState(false);
  const [terminalOutput, setTerminalOutput] = useState([]);
  const terminalRef = useRef(null);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1);
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          const yOffset = -100;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  // Simulate terminal output
  useEffect(() => {
    const outputs = [
      { text: `> Initializing ${tabsData[activeTab].name} service...`, type: 'info', delay: 0 },
      { text: `> Loading configuration files...`, type: 'info', delay: 500 },
      { text: `> Service ready!`, type: 'success', delay: 1000 },
    ];
    setTerminalOutput(outputs);
  }, [activeTab]);

  const tabsData = [
    { 
      name: "Web Development", 
      icon: "💻", 
      color: "#6366f1",
      gradient: "linear-gradient(135deg, #6366f1, #8b5cf6)",
      version: "v3.2.0",
      status: "active",
      code: `// 🌐 Web Development - Modern Web Applications
class WebApplication {
  constructor(name) {
    this.name = name;
    this.technologies = {
      frontend: ['React 18', 'Next.js 14', 'TypeScript'],
      backend: ['Node.js', 'Python', 'Go'],
      database: ['PostgreSQL', 'MongoDB', 'Redis'],
      devops: ['Docker', 'Kubernetes', 'AWS']
    };
    this.metrics = {
      performance: '99+ Lighthouse score',
      seo: 'Optimized',
      accessibility: 'WCAG 2.1 AAA',
      bestPractices: '100%'
    };
  }

  async build() {
    console.log(\`🚀 Building \${this.name} with cutting-edge tech stack\`);
    
    const project = await this.setupProject();
    await this.developFeatures();
    await this.optimize();
    
    return {
      status: '✅ Production Ready',
      performance: 'Blazing Fast',
      deployment: 'CI/CD Pipeline Active',
      monitoring: '24/7 Observability'
    };
  }

  developFeatures() {
    return [
      '✨ Interactive UI Components',
      '⚡ Server-Side Rendering',
      '🔒 JWT Authentication',
      '📱 Responsive Design',
      '🎨 Smooth Animations'
    ];
  }
}

// Initialize your next project
const app = new WebApplication('ZustandTech Platform');
const result = await app.build();
console.table(result);
// Output: Modern, scalable, production-ready applications` 
    },
    { 
      name: "Mobile Apps", 
      icon: "📱", 
      color: "#ec489a",
      gradient: "linear-gradient(135deg, #ec489a, #f472b6)",
      version: "v2.8.0",
      status: "active",
      code: `// 📱 Mobile App Development - Cross-Platform Excellence
class MobileAppDevelopment {
  constructor(platform = 'cross-platform') {
    this.platform = platform;
    this.frameworks = {
      reactNative: { performance: 'native-like', reuse: '90%' },
      flutter: { performance: '60fps', hotReload: true },
      native: { ios: 'SwiftUI', android: 'Jetpack Compose' }
    };
    this.features = [
      'Offline Support',
      'Push Notifications',
      'Biometric Auth',
      'Real-time Sync',
      'App Store Optimization'
    ];
  }

  async createApp() {
    console.log('🎨 Designing beautiful mobile experiences...');
    
    const architecture = this.designArchitecture();
    const ui = await this.createUI();
    const backend = await this.connectBackend();
    const testing = await this.runTests();
    
    return {
      app: 'Production Ready',
      stores: ['App Store', 'Google Play'],
      rating: '4.9+ Stars',
      downloads: 'Scalable to Millions'
    };
  }

  deploy() {
    return '📱 Ready for App Store & Google Play Submission';
  }
}

// Build your dream app
const mobileApp = new MobileAppDevelopment();
const app = await mobileApp.createApp();
console.log('✅ App ready for launch!');`
    },
    { 
      name: "Cloud Services", 
      icon: "☁️", 
      color: "#06b6d4",
      gradient: "linear-gradient(135deg, #06b6d4, #3b82f6)",
      version: "v4.1.0",
      status: "active",
      code: `// ☁️ Cloud Infrastructure - Enterprise-Grade Solutions
class CloudArchitecture {
  constructor(provider = 'multi-cloud') {
    this.provider = provider;
    this.services = {
      aws: ['EC2', 'Lambda', 'S3', 'RDS', 'EKS'],
      azure: ['VM', 'Functions', 'Blob', 'SQL', 'AKS'],
      gcp: ['Compute', 'Cloud Run', 'Storage', 'Spanner', 'GKE']
    };
    this.config = {
      autoScaling: true,
      loadBalancing: 'global',
      cdn: 'enabled',
      monitoring: '24/7',
      backup: 'automated'
    };
  }

  async deployApplication(app) {
    console.log(\`🚀 Deploying \${app} to \${this.provider} cloud\`);
    
    // Infrastructure as Code
    const infra = this.provisionInfrastructure();
    const container = await this.containerize();
    const pipeline = this.setupCICD();
    const monitoring = this.setupMonitoring();
    
    return {
      status: '✅ Deployed Successfully',
      url: 'https://your-app.cloud.com',
      uptime: '99.99% SLA',
      scalability: 'Auto-scales to demand'
    };
  }

  getBenefits() {
    return {
      cost: 'Pay-as-you-go',
      reliability: 'Multi-region backup',
      security: 'Enterprise-grade',
      performance: 'Global edge network'
    };
  }
}

// Launch your cloud infrastructure
const cloud = new CloudArchitecture('AWS');
const deployment = await cloud.deployApplication('Your App');
console.log(deployment);`
    },
    { 
      name: "UI/UX Design", 
      icon: "🎨", 
      color: "#f59e0b",
      gradient: "linear-gradient(135deg, #f59e0b, #ef4444)",
      version: "v1.9.0",
      status: "active",
      code: `// 🎨 UI/UX Design - User-Centered Design System
class DesignSystem {
  constructor(brand) {
    this.brand = brand;
    this.designTokens = {
      colors: {
        primary: '#6366f1',
        secondary: '#ec489a',
        accent: '#f59e0b'
      },
      typography: {
        fontFamily: ['Inter', 'system-ui'],
        scale: [12, 14, 16, 18, 24, 32, 48]
      },
      spacing: { unit: 8, scale: [4, 8, 12, 16, 24, 32, 48] },
      shadows: {
        sm: '0 1px 2px rgba(0,0,0,0.05)',
        md: '0 4px 6px rgba(0,0,0,0.07)',
        lg: '0 10px 15px rgba(0,0,0,0.1)'
      }
    };
  }

  async createDesignSystem() {
    const components = this.buildComponents();
    const prototypes = await this.createPrototypes();
    const testing = await this.userTesting();
    
    return {
      components: '50+',
      screens: 'Fully responsive',
      accessibility: 'WCAG 2.1 AAA',
      satisfaction: '95% user approval'
    };
  }

  exportTokens() {
    return this.designTokens;
  }
}

// Create beautiful, intuitive interfaces
const design = new DesignSystem('ZustandTech');
const system = await design.createDesignSystem();
console.log('🎨 Design System Ready for Development');`
    },
    { 
      name: "Fullstack", 
      icon: "⚡", 
      color: "#10b981",
      gradient: "linear-gradient(135deg, #10b981, #14b8a6)",
      version: "v3.0.0",
      status: "active",
      code: `// ⚡ Fullstack Development - End-to-End Solutions
class FullstackApplication {
  constructor(name) {
    this.name = name;
    this.stack = {
      frontend: { framework: 'React 18', state: 'Zustand', styling: 'Tailwind' },
      backend: { runtime: 'Node.js 20', framework: 'Express', auth: 'JWT' },
      database: { primary: 'PostgreSQL', cache: 'Redis' }
    };
  }

  async buildFullstack() {
    console.log('🔧 Setting up fullstack architecture...');
    
    const client = await this.setupFrontend();
    const server = await this.setupBackend();
    const database = await this.connectDatabase();
    const api = this.buildAPI();
    const auth = this.setupAuth();
    const deployment = await this.deploy();
    
    return {
      status: '✅ Fullstack App Ready',
      performance: 'Optimized',
      scalability: 'Horizontal scaling',
      monitoring: 'Real-time analytics'
    };
  }
}

// Create complete applications
const fullstack = new FullstackApplication('Your Business App');
const result = await fullstack.buildFullstack();
console.log('⚡ Fullstack Application Deployed!');`
    },
    { 
      name: "Performance Marketing", 
      icon: "📈", 
      color: "#ef4444",
      gradient: "linear-gradient(135deg, #ef4444, #f97316)",
      version: "v2.5.0",
      status: "active",
      code: `// 📈 Performance Marketing - Data-Driven Growth
class MarketingStrategy {
  constructor(goals) {
    this.goals = goals;
    this.channels = {
      seo: { organic: true, keywords: 'targeted' },
      social: { platforms: ['LinkedIn', 'Twitter', 'Instagram'] },
      paid: { google_ads: true, social_ads: true },
      email: { automation: true, segmentation: 'behavioral' }
    };
  }

  async launchCampaign() {
    console.log('📊 Analyzing market data...');
    
    const research = await this.marketResearch();
    const audience = this.defineTargetAudience();
    const seo = await this.optimizeSEO();
    const social = await this.runSocialCampaigns();
    const ads = await this.launchPaidAds();
    const analytics = this.trackPerformance();
    
    return {
      reach: '100k+ impressions',
      engagement: '25% increase',
      conversions: '3x growth',
      roi: '350% average'
    };
  }
}

// Drive business growth
const marketing = new MarketingStrategy({ leads: '1000+ monthly' });
const campaign = await marketing.launchCampaign();
console.log('📈 Campaign Launched! ROI increasing...');`
    }
  ];

  // Auto switch tabs
  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setActiveTab((prev) => (prev + 1) % tabsData.length);
      }, 6000);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(tabsData[activeTab].code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleTabClick = (index) => {
    setActiveTab(index);
    setIsPlaying(false);
    setTimeout(() => setIsPlaying(true), 10000);
  };

  const services = [
    {
      id: 1,
      title: 'Web Development',
      anchorId: 'web-development',
      description: 'Custom web applications built with modern technologies like React, Node.js and scalable backend systems.',
      icon: '💻',
      features: [
        'Responsive Web Design',
        'Progressive Web Apps',
        'E-commerce Solutions',
        'CMS Development',
        'API Integration'
      ],
      color: '#6366f1'
    },
    {
      id: 2,
      title: 'Mobile App Development',
      anchorId: 'mobile-apps',
      description: 'High-performance mobile applications for Android and iOS platforms.',
      icon: '📱',
      features: [
        'Android & iOS Apps',
        'React Native Apps',
        'Flutter Development',
        'App Store Optimization',
        'Mobile UI/UX Design'
      ],
      color: '#ec489a'
    },
    {
      id: 3,
      title: 'Cloud Solutions',
      anchorId: 'cloud-solutions',
      description: 'Secure and scalable cloud infrastructure using AWS, Azure and Google Cloud.',
      icon: '☁️',
      features: [
        'Cloud Migration',
        'DevOps & CI/CD',
        'Serverless Architecture',
        'Cloud Hosting',
        'Scalable Infrastructure'
      ],
      color: '#06b6d4'
    },
    {
      id: 4,
      title: 'UI/UX Design',
      anchorId: 'ui-ux-design',
      description: 'Beautiful and intuitive interfaces that deliver exceptional user experiences.',
      icon: '🎨',
      features: [
        'User Research',
        'Wireframing & Prototyping',
        'Visual Design',
        'Usability Testing',
        'Design Systems'
      ],
      color: '#f59e0b'
    },
    {
      id: 5,
      title: 'Fullstack Development',
      anchorId: 'fullstack',
      description: 'End-to-end development with modern frameworks and best practices.',
      icon: '⚡',
      features: [
        'Frontend & Backend',
        'Database Design',
        'API Development',
        'Microservices',
        'System Architecture'
      ],
      color: '#10b981'
    },
    {
      id: 6,
      title: 'Performance Marketing',
      anchorId: 'digital-marketing',
      description: 'Grow your business with SEO, social media marketing and paid ads.',
      icon: '📈',
      features: [
        'SEO Optimization',
        'Social Media Management',
        'Google Ads',
        'Content Marketing',
        'Brand Growth'
      ],
      color: '#ef4444'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="services-page"
    >
     <Helmet>

{/* PRIMARY SEO */}
<title>
  Software Development Services | React, AI, Mobile Apps & Cloud Solutions | ZustandTech
</title>

<meta
  name="description"
  content="ZustandTech provides professional software development services including React.js development, AI solutions, mobile app development, cloud computing, SaaS platforms, UI/UX design, fullstack development, and enterprise software solutions worldwide."
/>

<meta
  name="keywords"
  content="
    software development services,
    react development company,
    AI development company,
    mobile app development,
    cloud computing services,
    SaaS development company,
    enterprise software solutions,
    custom software development,
    Node.js development,
    MERN stack development,
    fullstack development,
    UI UX design company
  "
/>

<meta name="robots" content="index, follow" />

<meta
  name="googlebot"
  content="index, follow"
/>

<link
  rel="canonical"
  href="https://www.zustandtech.com/services"
/>

{/* OPEN GRAPH */}
<meta
  property="og:title"
  content="Software Development Services | ZustandTech"
/>

<meta
  property="og:description"
  content="Professional React.js, AI, cloud computing, SaaS, mobile app, and enterprise software development services."
/>

<meta
  property="og:image"
  content="https://www.zustandtech.com/ar.png"
/>

<meta
  property="og:url"
  content="https://www.zustandtech.com/services"
/>

<meta property="og:type" content="website" />

{/* TWITTER SEO */}
<meta
  name="twitter:card"
  content="summary_large_image"
/>

<meta
  name="twitter:title"
  content="Software Development Services | ZustandTech"
/>

<meta
  name="twitter:description"
  content="AI solutions, mobile app development, cloud computing, React.js development, and enterprise software services."
/>

<meta
  name="twitter:image"
  content="https://www.zustandtech.com/ar.png"
/>

{/* SERVICES SCHEMA */}
<script type="application/ld+json">
  {`
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Software Development Services",
      "provider": {
        "@type": "Organization",
        "name": "ZustandTech",
        "url": "https://www.zustandtech.com",
        "logo": "https://www.zustandtech.com/ar.png"
      },
      "areaServed": [
        "India",
        "United States",
        "United Kingdom",
        "Canada",
        "Australia",
        "Germany",
        "Singapore",
        "UAE"
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Software Development Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Web Development"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Mobile App Development"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Cloud Solutions"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "AI Development"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "UI UX Design"
            }
          }
        ]
      }
    }
  `}
</script>

</Helmet>

      <section className="services-list">
        <div className="container">
          <div className="page-heading">
            <h1>
              Software Development Services in India | 
              <span className="gradient-text"> ZustandTech</span>
            </h1>
            <p>
              We offer web development, mobile app development, AI solutions,
              cloud computing and digital marketing services to help your business grow.
            </p>
          </div>

          {/* BIG TERMINAL */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className={`big-terminal ${isMaximized ? 'maximized' : ''}`}
          >
            {/* Terminal Header */}
            <div className="terminal-header-big">
              <div className="terminal-controls-big">
                <motion.div 
                  className="terminal-dot-big red"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => console.log('Close')}
                />
                <motion.div 
                  className="terminal-dot-big yellow"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => console.log('Minimize')}
                />
                <motion.div 
                  className="terminal-dot-big green"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setIsMaximized(!isMaximized)}
                />
              </div>
              
              <div className="terminal-title-big">
                <FaTerminal />
                <span>zustandtech@developer:~/services</span>
                <span className="terminal-version">v2.0.0</span>
              </div>
              
              <div className="terminal-actions-big">
                <motion.button 
                  className="terminal-action-big"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handlePlayPause}
                  title={isPlaying ? "Pause" : "Play"}
                >
                  {isPlaying ? <FaPause /> : <FaPlay />}
                </motion.button>
                <motion.button 
                  className="terminal-action-big"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleCopyCode}
                  title="Copy code"
                >
                  {copied ? <FaCheck /> : <FaCopy />}
                </motion.button>
                <motion.button 
                  className="terminal-action-big"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsMaximized(!isMaximized)}
                  title={isMaximized ? "Minimize" : "Maximize"}
                >
                  {isMaximized ? <FaWindowMinimize /> : <FaWindowMaximize />}
                </motion.button>
              </div>
            </div>

            {/* Terminal Tabs */}
            <div className="terminal-tabs-big">
              {tabsData.map((tab, index) => (
                <motion.button
                  key={index}
                  className={`terminal-tab-big ${index === activeTab ? 'active' : ''}`}
                  onClick={() => handleTabClick(index)}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    background: index === activeTab ? tab.gradient : 'transparent'
                  }}
                >
                  <span className="tab-icon-big">{tab.icon}</span>
                  <span className="tab-name-big">{tab.name}</span>
                  <span className="tab-version">{tab.version}</span>
                  {index === activeTab && (
                    <motion.span 
                      className="tab-indicator-big"
                      layoutId="activeTabBig"
                      transition={{ type: "spring", duration: 0.5 }}
                    />
                  )}
                </motion.button>
              ))}
            </div>

            {/* Terminal Body */}
            <div className="terminal-body-big">
              {/* Status Line */}
              <div className="terminal-status-line">
                <span className="status-prompt">┌─[</span>
                <span className="status-user">zustandtech</span>
                <span className="status-at">@</span>
                <span className="status-host">developer</span>
                <span className="status-prompt">]─[</span>
                <span className="status-path">~/services</span>
                <span className="status-prompt">]</span>
              </div>
              <div className="terminal-status-line">
                <span className="status-prompt">└─[</span>
                <span className="status-time">{new Date().toLocaleTimeString()}</span>
                <span className="status-prompt">]─[</span>
                <span className="status-service">{tabsData[activeTab].name}</span>
                <span className="status-prompt">]</span>
              </div>
              
              <div className="terminal-divider">━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━</div>
              
              {/* Terminal Output */}
              <div className="terminal-output">
                {terminalOutput.map((output, idx) => (
                  <div key={idx} className={`terminal-output-line ${output.type}`}>
                    <span className="output-prompt">$</span>
                    <span className="output-text">{output.text}</span>
                  </div>
                ))}
              </div>

              {/* Code Display */}
              <div className="terminal-code-big">
                <div className="code-header-big">
                  <FaCode />
                  <span>{tabsData[activeTab].name}.service.js</span>
                </div>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.4 }}
                    className="code-content-big"
                  >
                    <pre className="code-syntax-big">
                      <TypeAnimation
                        sequence={[
                          tabsData[activeTab].code,
                          5000,
                        ]}
                        wrapper="span"
                        speed={65}
                        repeat={Infinity}
                        className="typing-animation-big"
                      />
                    </pre>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Terminal Footer */}
              <div className="terminal-footer-big">
                <div className="terminal-status-big">
                  <motion.span 
                    className="status-led"
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                  <span className="status-text">Service {tabsData[activeTab].status}</span>
                  <span className="status-separator">|</span>
                  <span className="status-text">Memory: 42MB</span>
                  <span className="status-separator">|</span>
                  <span className="status-text">CPU: 3%</span>
                </div>
                <div className="terminal-cursor-big">
                  <span className="cursor-text">_</span>
                </div>
              </div>
            </div>
          </motion.div>

{/* SEO CONTENT */}
<section className="services-seo-content">
  <div className="container">

    <h2>
      Professional Software Development Services
    </h2>

    <p>
      ZustandTech is a global software development company specializing
      in React.js development, Node.js backend systems,
      AI-powered applications, mobile app development,
      SaaS platforms, cloud infrastructure,
      UI/UX design, and enterprise software solutions.
    </p>

    <h2>
      React.js, AI & Fullstack Development Experts
    </h2>

    <p>
      Our expert developers create scalable web applications,
      cloud-native systems, AI automation tools,
      ERP platforms, CRM software,
      and high-performance digital products using
      modern technologies like React.js, Next.js,
      Node.js, Python, AWS, and cloud computing.
    </p>

  </div>
</section>

          {/* Services Grid */}
          <div className="services-grid-detailed">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                id={service.anchorId}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="service-detailed-card"
                whileHover={{ y: -8 }}
                style={{ borderTopColor: service.color }}
              >
                <div className="service-detailed-header">
                  <motion.div 
                    className="service-detailed-icon"
                    whileHover={{ rotate: 5, scale: 1.1 }}
                    style={{ background: `${service.color}10`, color: service.color }}
                  >
                    {service.icon}
                  </motion.div>
                  <h2>{service.title}</h2>
                </div>

                <p>{service.description}</p>

                <ul className="service-features-list">
                  {service.features.map((feature, i) => (
                    <motion.li 
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 + i * 0.05 }}
                    >
                      <FaCheckCircle style={{ color: service.color }} />
                      {feature}
                    </motion.li>
                  ))}
                </ul>

                {/* <Link to="/contact" className="service-detailed-link">
                  Get Started <FaArrowRight />
                </Link> */}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="services-cta-section">
        <div className="container">
          <h2>Ready to Build Your Project?</h2>
          <p>Contact us today and let's grow your business together.</p>
          <Link to="/contact" className="btn btn-primary">
            Contact Us <FaArrowRight />
          </Link>
        </div>
      </section>
    </motion.div>
  );
};

export default ServicesPage;